// فحوص الوجه للصورة المرجعية عبر MediaPipe Face Landmarker (تعمل على الجهاز، لا شيء يُرفع).
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

export const TOTAL_CHECKS = 5;
const WINDOW_ASPECT = 220 / 292;
const FACE_MIN_HEIGHT = 0.36, FACE_MAX_HEIGHT = 0.72;
const CENTER_TOLERANCE_X = 0.14, CENTER_TOLERANCE_Y = 0.14, CENTER_Y = 0.47;
const LIGHT_LOW = 70, LIGHT_GREAT = 125;
const STEADY_MOVE = 0.05, STEADY_MS = 150, EYE_CLOSED = 0.5;
const WASM_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm";
const MODEL_URL = "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

export const INITIAL_RESULT = { passing: 0, hint: "noFace", lighting: "waiting", distance: "waiting", stability: "waiting" };

export function sameResult(a, b) {
  return a.passing === b.passing && a.hint === b.hint && a.lighting === b.lighting && a.distance === b.distance && a.stability === b.stability;
}

const blink = (bs, name) => {
  const c = bs && bs.categories && bs.categories.find((k) => k.categoryName === name);
  return c ? c.score : 0;
};
const bboxOf = (lm, vw, vh) => {
  let a = 1, b = 1, c = 0, d = 0;
  for (const p of lm) { if (p.x < a) a = p.x; if (p.x > c) c = p.x; if (p.y < b) b = p.y; if (p.y > d) d = p.y; }
  return { x: a * vw, y: b * vh, w: (c - a) * vw, h: (d - b) * vh };
};

export async function createFaceChecker() {
  const vision = await FilesetResolver.forVisionTasks(WASM_URL);
  let detector;
  const opts = (delegate) => ({ baseOptions: { modelAssetPath: MODEL_URL, delegate }, runningMode: "VIDEO", numFaces: 2, outputFaceBlendshapes: true });
  try { detector = await FaceLandmarker.createFromOptions(vision, opts("GPU")); }
  catch { detector = await FaceLandmarker.createFromOptions(vision, opts("CPU")); }

  const probe = document.createElement("canvas"); probe.width = 24; probe.height = 24;
  const pctx = probe.getContext("2d", { willReadFrequently: true });
  let last = null, stillSince = 0, lastTs = 0;

  const brightness = (video, sx, sy, sw, sh) => {
    if (!pctx || sw < 2 || sh < 2) return 128;
    pctx.drawImage(video, sx, sy, sw, sh, 0, 0, 24, 24);
    const d = pctx.getImageData(0, 0, 24, 24).data;
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) sum += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
    return sum / (d.length / 4);
  };

  return {
    analyze(video, now) {
      const vw = video.videoWidth, vh = video.videoHeight;
      if (!vw || !vh) return INITIAL_RESULT;
      let visW = vw, visH = vh, x0 = 0, y0 = 0;
      if (vw / vh > WINDOW_ASPECT) { visW = vh * WINDOW_ASPECT; x0 = (vw - visW) / 2; }
      else { visH = vw / WINDOW_ASPECT; y0 = (vh - visH) / 2; }
      const ts = Math.max(now, lastTs + 1); lastTs = ts;
      const res = detector.detectForVideo(video, ts);
      const faces = res.faceLandmarks || [];
      if (faces.length === 0) {
        last = null; stillSince = 0;
        const lum = brightness(video, x0, y0, visW, visH);
        return { passing: 0, hint: lum < LIGHT_LOW ? "tooDark" : "noFace", lighting: lum < LIGHT_LOW ? "low" : "waiting", distance: "waiting", stability: "waiting" };
      }
      if (faces.length > 1) { last = null; stillSince = 0; return { passing: 0, hint: "manyFaces", lighting: "waiting", distance: "waiting", stability: "waiting" }; }
      const bb = bboxOf(faces[0], vw, vh);
      const cx = (bb.x + bb.w / 2 - x0) / visW, cy = (bb.y + bb.h / 2 - y0) / visH, size = bb.h / visH;
      const lum = brightness(video, bb.x, bb.y, bb.w, bb.h);
      const lighting = lum < LIGHT_LOW ? "low" : lum >= LIGHT_GREAT ? "great" : "good";
      const distance = size < FACE_MIN_HEIGHT ? "far" : size > FACE_MAX_HEIGHT ? "close" : "fit";
      const centred = Math.abs(cx - 0.5) <= CENTER_TOLERANCE_X && Math.abs(cy - CENTER_Y) <= CENTER_TOLERANCE_Y;
      const bs = (res.faceBlendshapes || [])[0];
      const eyesOpen = blink(bs, "eyeBlinkLeft") < EYE_CLOSED && blink(bs, "eyeBlinkRight") < EYE_CLOSED;
      const moved = last ? Math.hypot(cx - last.cx, cy - last.cy) + Math.abs(size - last.size) : 1;
      if (moved > STEADY_MOVE || !stillSince) stillSince = now;
      last = { cx, cy, size };
      const lightOk = lighting !== "low", distOk = distance === "fit", steady = now - stillSince >= STEADY_MS;
      const ok = steady && lightOk && distOk && centred && eyesOpen;
      const passing = 1 + (distOk ? 1 : 0) + (centred ? 1 : 0) + (lightOk ? 1 : 0) + (ok ? 1 : 0);
      let hint = "ready";
      if (!lightOk) hint = "tooDark";
      else if (distance === "far") hint = "tooFar";
      else if (distance === "close") hint = "tooClose";
      else if (!centred) hint = "center";
      else if (!eyesOpen) hint = "openEyes";
      else if (!steady) hint = "lookAhead";
      return { passing, hint, lighting, distance, stability: ok ? "steady" : "checking" };
    },
    reset() { last = null; stillSince = 0; },
    close() { detector.close(); },
  };
}
